import React, { useState, useMemo, useEffect } from "react";
import cs from "classnames";
import { NavLink } from "react-router-dom";
import { useRouter } from "../../../../../lib/hooks/useRouter";
import { useTemplateContext } from "../../../../../lib/context/Templates/Templates/TemplatesContext";
import { capitalizeFirstLetter } from "../../../../../lib/helpers/capitalizeFirstLetter";
import { collectAllChildren } from "../../../../../lib/helpers/collectWorkspaceTreeChildren";

import { WorkplaceFields } from "../../../../../api/graphql/workplaces";
import { getWorkplaceChildren } from "../../../../Workplaces/helpers/getWorkplaceChildren";
import { getWorkplaceIcon } from "../../../../Workplaces/helpers/getWorkplaceInformation";
import { Icon } from "../../../../shared/Icon/Icon";

interface Props {
  item: WorkplaceFields;
  toggleParent: (value: boolean) => void;
  filterWorkplace: "Room" | "Desk";
  allToggled?: boolean;
}

export const ItemsWorkspaceSelector = ({
  item,
  toggleParent,
  filterWorkplace,
  allToggled,
}: Props) => {
  const { pathname } = useRouter();
  const { activeProfile, pickedWorkspaces, setPickedWorkspaces } =
    useTemplateContext();
  const isActive = pathname.includes(item.id);
  const [toggle, setToggle] = useState(() => isActive);

  useEffect(() => {
    if (allToggled !== undefined) {
      setToggle(allToggled);
    }
  }, [allToggled]);

  const targetType = useMemo(
    () => capitalizeFirstLetter(activeProfile?.type || ""),
    [activeProfile]
  );

  const allTargetTypeDescendants = useMemo(() => {
    if (!targetType) return [];
    const descendants = [item, ...collectAllChildren(item)].filter(
      (child) => child.__typename === targetType
    );
    return descendants.length > 0
      ? descendants
      : [item].filter((child) => child.__typename === targetType);
  }, [item, targetType]);

  const isSelected = useMemo(() => {
    return allTargetTypeDescendants.some((descendant) =>
      pickedWorkspaces.data.some((picked) => picked.id === descendant.id)
    );
  }, [allTargetTypeDescendants, pickedWorkspaces.data]);

  const icon = getWorkplaceIcon(item.__typename);
  const workplaceChildren = getWorkplaceChildren(item);
  const hasChildren = workplaceChildren.some(
    (childGroup) => childGroup && childGroup.length > 0
  );

  const handleToggle = () => {
    setToggle((prev) => !prev);
  };

  const handleWorkspaceClick = () => {
    if (!targetType) return;
    if (!isSelected) {
      const newWorkspaces = allTargetTypeDescendants.filter(
        (w) => !pickedWorkspaces.data.some((picked) => picked.id === w.id)
      );
      setPickedWorkspaces({
        data: [...pickedWorkspaces.data, ...newWorkspaces],
      });
    } else {
      const descendantIdsToRemove = allTargetTypeDescendants.map((i) => i.id);
      const updatedWorkspaces = pickedWorkspaces.data.filter(
        (workspace) => !descendantIdsToRemove.includes(workspace.id)
      );
      setPickedWorkspaces({ data: updatedWorkspaces });
    }
  };

  const classes = cs("WorkplacesTreeLevel", {
    "WorkplacesTreeLevel--open": toggle,
    "WorkplacesTreeLevel--active": isActive,
    "WorkplacesTreeLevel--space": !hasChildren,
  });

  const subLevelClasses = cs("WorkplacesTreeLevel__sub-level", {
    "WorkplacesTreeLevel__sub-level--open": toggle,
  });

  return (
    <>
      <div className={classes}>
        <div className="WorkplacesTreeLevel__wrapper">
          {hasChildren && (
            <span
              onClick={handleToggle}
              className="WorkplacesTreeLevel__toggle"
            ></span>
          )}
          <NavLink
            className="WorkplacesTreeLevel__link"
            onClick={handleWorkspaceClick}
            to={"#"}
          >
            <div className="WorkplacesTreeLevel__icon">
              <Icon icon={icon} />
            </div>
            <h3 className="WorkplacesTreeLevel__name">{item.name}</h3>
          </NavLink>
          <div
            className="WorkplacesTreeLevel__status"
            onClick={handleWorkspaceClick}
          >
            <Icon icon={isSelected ? "checkbox-full" : "checkbox-empty"} />
          </div>
        </div>
      </div>
      {hasChildren && (
        <div className={subLevelClasses}>
          {workplaceChildren.map((child) => {
            return child
              ?.filter(
                (i) =>
                  i.__typename !== filterWorkplace &&
                  i.__typename !== "Wayfinder"
              )
              ?.map((workplace) => (
                <ItemsWorkspaceSelector
                  key={workplace.id}
                  item={workplace}
                  toggleParent={(value) => {
                    setToggle(value);
                    toggleParent(value);
                  }}
                  filterWorkplace={filterWorkplace}
                  allToggled={allToggled}
                />
              ));
          })}
        </div>
      )}
    </>
  );
};
